import React, { useEffect, useState } from 'react';
import { Tabs, Form, Input, Button, Row, Col, Select, Checkbox, Radio, DatePicker } from 'antd';
import * as S from './CustomerLogin.styles';
import daphLogo from './../../../assets/images/logo.jpg';
import mainLogo from './../../../assets/images/logo_crop.png';
import { useLocation, useNavigate } from 'react-router-dom';
import { registerCustomer } from '@app/api/customer.api';
import { login } from '@app/api/auth.api';
import { notificationController } from '@app/controllers/notificationController';
import { useAppDispatch } from '@app/hooks/reduxHooks';
import { doLogin } from '@app/store/slices/authSlice';
import { UserOutlined } from '@ant-design/icons';

const { TabPane } = Tabs;
const { Option } = Select;

export const CustomerLogin = () => {
  const [activeTab, setActiveTab] = useState('regular');
  const [isRegister, setIsRegister] = useState(false);
  const [accountType, setAccountType] = useState('INDIVIDUAL');
  const location = useLocation();
  const navigate = useNavigate();

  const dispatch = useAppDispatch();
  const [isLoading, setLoading] = useState(false);
  const [isRegisterLoading, setRegisterLoading] = useState(false);

  useEffect(() => {
    if (location.pathname === '/register') {
      handleFormSwitch();
    }
  }, []);

  const handleTabChange = (key: string) => {
    console.log(location);
    setActiveTab(key);
  };

  const handleFormSwitch = () => {
    setIsRegister(!isRegister);
  };

  const handleAccountTypeChange = (value: string) => {
    setAccountType(value);
  };

  const handleSubmit = async (values: any) => {
    // try {
    //   const response = await login(values);

    //   if (response.token) {
    //     localStorage.setItem('token', response.token);

    //     notificationController.success({ message: 'Login successful' });

    //     navigate('/');
    //   }
    // } catch (error: any) {
    //   notificationController.error({
    //     message: error.message || 'Login failed. Please try again.',
    //   });
    // }
    setLoading(true);
    dispatch(doLogin({ loginPayload: values, loginType: 'customer' }))
      .unwrap()
      .then(() => {
        navigate('/');
      })
      .catch((err) => {
        console.log('Login error caught:', err);
        notificationController.error({ message: err.message });
        setLoading(false);
      });
  };

  const registerRegularCustomer = (values: any): void => {
    setRegisterLoading(true);
    registerCustomer({
      ...values,
      customer_type: 'REGULAR',
      dob: values.dob.format('YYYY-MM-DD'),
      varyfied: values.email,
      has_br: values.business_type === 'BUSINESS' ? 'YES' : 'NO',
    })
      .then(() => {
        notificationController.success({ message: 'Registration successful' });
        handleFormSwitch();
      })
      .catch((error) => {
        notificationController.error({ message: error.message });
      })
      .finally(() => {
        setRegisterLoading(false);
      });
  };

  const registerOneTimeCustomer = (values: any): void => {
    setRegisterLoading(true);
    registerCustomer({
      ...values,
      dob: values.dob.format('YYYY-MM-DD'),
      customer_type: 'ONETIME',
      varyfied: values.email,
      business_type: 'INDIVIDUAL',
      business_name: '',
      business_reg_no: '',
      business_address: '',
      company_type: 'SOLEPRO',
    })
      .then(() => {
        notificationController.success({ message: 'Registration successful' });
        handleFormSwitch();
      })
      .catch((error) => {
        notificationController.error({ message: error.message });
      })
      .finally(() => {
        setRegisterLoading(false);
      });
  };

  // Login Form
  const LoginForm: React.FC<{ onFinish: (values: any) => void; onSwitch: () => void }> = ({ onFinish, onSwitch }) => (
    <Form
      layout="vertical"
      onFinish={(values) => {
        console.log('Form onFinish triggered');
        onFinish(values);
        return false; // Explicitly return false
      }}
    >
      <Form.Item name="username" label="Username" rules={[{ required: true, message: 'Please enter your username!' }]}>
        <Input placeholder="Enter your username" />
      </Form.Item>
      <Form.Item name="password" label="Password" rules={[{ required: true, message: 'Please enter your password!' }]}>
        <Input.Password placeholder="Enter your password" />
      </Form.Item>
      <S.ButtonWrapper>
        <Form.Item>
          <Button type="primary" htmlType="submit" loading={isLoading}>
            Login
          </Button>
          <S.RegisterLink>
            Are you a new user?{' '}
            <span onClick={onSwitch} style={{ cursor: 'pointer', color: 'blue' }}>
              Click here to Create Account
            </span>
          </S.RegisterLink>
        </Form.Item>
        {/* <S.ActionButtons> */}
        {/* <Button htmlType="button">External Login</Button> */}
        <span>
          <Button
            size="small"
            htmlType="button"
            icon={<UserOutlined />}
            onClick={() => navigate('/auth/login')}
            style={{ maxWidth: '150px' }}
          >
            Admin Login
          </Button>
        </span>
        {/* </S.ActionButtons> */}
      </S.ButtonWrapper>
    </Form>
  );

  // Regular Customer Register Form
  const RegisterForm: React.FC<{
    accountType: string;
    onAccountTypeChange: (value: string) => void;
    onFinish: (values: any) => void;
    onSwitch: () => void;
    isLoading: boolean;
  }> = ({ accountType, onAccountTypeChange, onFinish, onSwitch, isLoading }) => {
    const [form] = Form.useForm();
    const termsChecked = Form.useWatch('terms', form);

    return (
      <Form layout="vertical" form={form} onFinish={onFinish} id="registrationForm" size="small">
        <Form.Item name="business_type" label="Account Type" initialValue={accountType}>
          <Select onChange={onAccountTypeChange} data-testid="account-type">
            <Option value="INDIVIDUAL">Individual</Option>
            <Option value="BUSINESS">Business</Option>
          </Select>
        </Form.Item>

        {accountType === 'BUSINESS' && (
          <>
            <Form.Item name="business_name" label="Business Name" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item name="business_address" label="Business Address" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item name="company_type" label="Company Type" rules={[{ required: true }]}>
              <Select>
                <Option value="LLC">LLC</Option>
                <Option value="CORP">Corporation</Option>
                <Option value="SOLEPRO">Sole Proprietorship</Option>
                <Option value="OTHER">Other</Option>
              </Select>
            </Form.Item>
            <Form.Item name="business_reg_no" label="Business Registration No" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <h3 style={{ textAlign: 'left' }}>Business Representative</h3>
            <br />
          </>
        )}
        <Row gutter={12}>
          <Col xs={24} sm={12}>
            <Form.Item name="first_name" label="First Name" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="last_name" label="Last Name" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col xs={24} sm={12}>
            <Form.Item name="username" label="Username" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}>
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col xs={24} sm={12}>
            <Form.Item name="contact_no" label="Contact No" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="dob" label="Date of Birth" rules={[{ required: true }]}>
              <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
            </Form.Item>
          </Col>
        </Row>
        {accountType === 'INDIVIDUAL' && (
          <>
            <Form.Item name="address" label="Address" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item name="nic" label="NIC/Passport No" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
          </>
        )}
        {accountType === 'BUSINESS' && (
          <>
            <Form.Item name="nic" label="NIC/Passport No" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item name="address" label="Address" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
          </>
        )}
        <Row gutter={12}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="password"
              label="Password"
              rules={[
                { required: true },
                {
                  pattern: /^(?=.*[A-Z])(?=.*[!@#$%^&*])(?=.*[0-9])(?=.*[a-zA-Z]).{8,}$/,
                  message:
                    'Password must contain at least 8 characters including an uppercase letter, a special character, and a number',
                },
              ]}
            >
              <Input.Password />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="password_confirmation"
              label="Confirm Password"
              dependencies={['password']}
              rules={[
                { required: true, message: 'Please confirm your password!' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Passwords do not match!'));
                  },
                }),
              ]}
            >
              <Input.Password iconRender={() => null} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Form.Item
            name="terms"
            valuePropName="checked"
            rules={[{ required: true, message: 'Please agree to the Terms & Conditions!' }]}
          >
            <Checkbox>
              I agree to the{' '}
              <a href="/docs/terms-and-conditions.pdf" target="_blank" rel="noopener noreferrer">
                Terms and Conditions
              </a>
            </Checkbox>
          </Form.Item>
        </Row>
        <S.ButtonWrapper>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={isLoading} disabled={!termsChecked}>
              Register
            </Button>
          </Form.Item>
          <S.RegisterLink>
            <Form.Item>
              Already have an account?{' '}
              <span onClick={onSwitch} style={{ cursor: 'pointer', color: 'blue' }}>
                Click here to Login
              </span>
            </Form.Item>
          </S.RegisterLink>
        </S.ButtonWrapper>
      </Form>
    );
  };

  // One-Time Customer Register Form
  // const OneTimeCustomerForm: React.FC<{
  //   onFinish: (values: any) => void;
  //   onSwitch: () => void;
  //   isLoading: boolean;
  // }> = ({ onFinish, onSwitch, isLoading }) => (
  //   <Form layout="vertical" onFinish={onFinish} id="registrationForm">
  //      <Row gutter={12}>
  //       <Col xs={24} sm={12}>
  //         <Form.Item name="first_name" label="First Name" rules={[{ required: true }]}>
  //           <Input />
  //         </Form.Item>
  //       </Col>
  //       <Col xs={24} sm={12}>
  //         <Form.Item name="last_name" label="Last Name" rules={[{ required: true }]}>
  //           <Input />
  //         </Form.Item>
  //       </Col>
  //     </Row>
  //      <Row gutter={12}>
  //       <Col xs={24} sm={12}>
  //         <Form.Item name="username" label="Username" rules={[{ required: true }]}>
  //           <Input />
  //         </Form.Item>
  //       </Col>
  //       <Col xs={24} sm={12}>
  //         <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}>
  //           <Input />
  //         </Form.Item>
  //       </Col>
  //     </Row>
  //      <Row gutter={12}>
  //       <Col xs={24} sm={12}>
  //         <Form.Item name="contact_no" label="Contact No" rules={[{ required: true }]}>
  //           <Input />
  //         </Form.Item>
  //       </Col>
  //       <Col xs={24} sm={12}>
  //         <Form.Item name="dob" label="Date of Birth" rules={[{ required: true }]}>
  //           <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
  //         </Form.Item>
  //       </Col>
  //       <Col xs={24} sm={24}>
  //         <Form.Item name="address" label="Address" rules={[{ required: true }]}>
  //           <Input />
  //         </Form.Item>
  //       </Col>
  //       <Col xs={24} sm={24}>
  //         <Form.Item name="nic" label="NIC No" rules={[{ required: true }]}>
  //           <Input />
  //         </Form.Item>
  //       </Col>
  //     </Row>

  //      <Row gutter={12}>
  //       <Col xs={24} sm={12}>
  //         <Form.Item
  //           name="password"
  //           label="Password"
  //           rules={[
  //             { required: true },
  //             {
  //               pattern: /^(?=.*[A-Z])(?=.*[!@#$%^&*])(?=.*[0-9])(?=.*[a-zA-Z]).{8,}$/,
  //               message:
  //                 'Password must contain at least 8 characters including an uppercase letter, a special character, and a number',
  //             },
  //           ]}
  //         >
  //           <Input.Password />
  //         </Form.Item>
  //       </Col>
  //       <Col xs={24} sm={12}>
  //         <Form.Item
  //           name="password_confirmation"
  //           label="Confirm Password"
  //           dependencies={['password']}
  //           rules={[
  //             { required: true, message: 'Please confirm your password!' },
  //             ({ getFieldValue }) => ({
  //               validator(_, value) {
  //                 if (!value || getFieldValue('password') === value) {
  //                   return Promise.resolve();
  //                 }
  //                 return Promise.reject(new Error('Passwords do not match!'));
  //               },
  //             }),
  //           ]}
  //         >
  //           <Input.Password iconRender={() => null} />
  //         </Form.Item>
  //       </Col>
  //     </Row>
  //     <Form.Item
  //       name="terms"
  //       valuePropName="checked"
  //       rules={[{ required: true, message: 'Please agree to the Terms & Conditions!' }]}
  //     >
  //       <Checkbox>I agree to the Terms and Conditions</Checkbox>
  //     </Form.Item>
  //     <S.ButtonWrapper>
  //       <Form.Item>
  //         <Button type="primary" htmlType="submit" loading={isLoading}>
  //           Register
  //         </Button>
  //       </Form.Item>
  //       <S.RegisterLink>
  //         <Form.Item>
  //           Already have an account?{' '}
  //           <span onClick={onSwitch} style={{ cursor: 'pointer', color: 'blue' }}>
  //             Click here to Login
  //           </span>
  //         </Form.Item>
  //       </S.RegisterLink>
  //     </S.ButtonWrapper>
  //   </Form>
  // );

  return (
    <S.PageWrapper>
      <S.LeftSection />
      <S.RightSection>
        <S.TopLogo>
          <img src={daphLogo} alt="Top Logo" />
        </S.TopLogo>

        <S.ContentWrapper isRegister={isRegister}>
          <S.MainLogo>
            <img src={mainLogo} alt="Main Logo" />
          </S.MainLogo>

          <S.FormWrapper isRegister={isRegister}>
            <h1>{isRegister ? 'CREATE ACCOUNT' : 'LOGIN'}</h1>
            {/* {isRegister ? (
              <Tabs activeKey={activeTab} onChange={handleTabChange} centered>
                <TabPane tab="Regular Customer" key="regular">
                  <S.ScrollableForm>
                    <RegisterForm
                      key={activeTab}
                      accountType={accountType}
                      onAccountTypeChange={handleAccountTypeChange}
                      onFinish={registerRegularCustomer}
                      onSwitch={handleFormSwitch}
                      isLoading={isRegisterLoading}
                    />
                  </S.ScrollableForm>
                </TabPane>
                <TabPane tab="One Time Customer" key="onetime">
                  <S.ScrollableForm>
                    <OneTimeCustomerForm
                      onFinish={registerOneTimeCustomer}
                      onSwitch={handleFormSwitch}
                      isLoading={isRegisterLoading}
                    />
                  </S.ScrollableForm>
                </TabPane>
              </Tabs>
            ) : ( */}

            {/* new code */}
            {isRegister ? (
              <S.ScrollableForm>
                <RegisterForm
                  accountType={accountType}
                  onAccountTypeChange={handleAccountTypeChange}
                  onFinish={registerRegularCustomer}
                  onSwitch={handleFormSwitch}
                  isLoading={isRegisterLoading}
                />
              </S.ScrollableForm>
            ) : (
              <S.ScrollableForm>
                <LoginForm onFinish={handleSubmit} onSwitch={handleFormSwitch} />
              </S.ScrollableForm>
            )}
          </S.FormWrapper>
        </S.ContentWrapper>
      </S.RightSection>
    </S.PageWrapper>
  );
};

export default CustomerLogin;
